import { Divider, Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import Icon from "../../../../../Icon"

import styles from "./receiptCard.module.less"

const { Title, Text } = Typography

// Sample transaction values for the preview (these are per-transaction, not part
// of the profile; they show the format a cardholder sees in their banking app).
export const SAMPLE = {
  amount: "$96.99",
  paidOn: "18 Jun 2026",
  receiptNo: "25322-35533",
  card: "2453"
}

export const formatPhone = (p) => {
  if (!p) return ""
  if (typeof p === "string") return p
  if (p.number) return `+${p.code || ""} ${p.number}`.trim()
  return ""
}

// Live-preview footer address row (CHA1-1523 §11.8 BLOCK 8.4). Composed from
// the 6-field address block on Step 2. Empty parts are dropped together with
// their surrounding separator, and State / Region is skipped entirely when
// Country code is not US so a non-US preview reads "{address}, {city},
// {postal}, {country_code}" without the phantom comma.
export const composeAddress = (details = {}) => {
  const address = String(details.business_address ?? "").trim()
  const city = String(details.city ?? "").trim()
  const postal = String(details.postal_code ?? "").trim()
  const countryCode = String(details.country_code ?? "")
    .trim()
    .toUpperCase()
  const state = countryCode === "US" ? String(details.state_region ?? "").trim() : ""

  const statePostal = [state, postal].filter(Boolean).join(" ")
  return [address, city, statePostal, countryCode].filter(Boolean).join(", ")
}

// Policy/support fields accept a bare host ("example.com/refunds"): prepend
// https:// so the browser doesn't treat it as a relative path from this app.
// Any explicit scheme the user typed (http://, https://, mailto:, tel:, ...)
// is kept as-is, and protocol-relative "//host" is upgraded to https.
export const withScheme = (u) => {
  const s = String(u || "").trim()
  if (!s) return ""
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return s
  if (s.startsWith("//")) return `https:${s}`
  return `https://${s}`
}

// The "digital receipt" a cardholder sees in their banking app: a centered
// identity + hero amount, a short itemized ledger, and a merchant/support
// footer. It reflects every profile field the merchant fills in (each shown
// only when present). Shared by the Review step (flat surface) and the live
// preview (frosted-glass surface via `preview`). `highlight` is the section key
// currently being edited; the matching region gets a soft highlight so the
// merchant sees which fields they are changing.
const ReceiptCard = ({ buildingBlocks, highlight, preview }) => {
  const { t } = useTranslation()
  const bb = buildingBlocks || {}
  const biz = bb.business_info || {}
  const details = bb.business_details || {}
  const support = bb.support_legal || {}
  const logoUrl = details.business_logo?.url

  const businessName = biz.business_name || t("Your business")
  const businessWebsite = biz.business_website || ""
  const businessDescription = biz.business_description || ""
  // The label is static: CHA1-3169 removed the Product/service name field, so only the
  // merchant-written description stands behind this row.
  const productDescription = details.product_description || ""
  const businessAddress = composeAddress(details)
  const supportPhone = formatPhone(support.support_phone)
  const supportEmail = support.support_email || ""

  // Soft highlight on the region tied to the step the merchant is on.
  const hl = (section) => (highlight === section ? styles.highlight : "")

  return (
    <div className={cls(styles.card, preview ? styles.cardGlass : styles.cardFlat)}>
      {/* Identity + hero amount, all centered on one spine. */}
      <Flex vertical align="center" gap={12} className={styles.hero}>
        <div className={cls(styles.logo, hl("business_details"))}>
          {logoUrl ? (
            <img src={logoUrl} alt={businessName} />
          ) : (
            <Icon type="receipt_bill" style={{ fontSize: 20 }} />
          )}
        </div>
        <div className={cls(styles.identity, hl("business_info"))}>
          <Text className={styles.name}>{businessName}</Text>
          {businessWebsite ? (
            <Text type="secondary" className={styles.website}>
              {businessWebsite}
            </Text>
          ) : null}
          {businessDescription ? (
            <Text type="secondary" className={styles.bizdesc}>
              {businessDescription}
            </Text>
          ) : null}
        </div>
        <div className={styles.amountWrap}>
          <Title level={2} className={styles.amount}>
            {SAMPLE.amount}
          </Title>
          <Text type="secondary" className={styles.paid}>
            {t("Paid _date_", { date: SAMPLE.paidOn })}
          </Text>
        </div>
      </Flex>

      <Divider style={{ margin: "24px 0" }} />

      {/* Order ledger: label left, value right. */}
      <Flex vertical gap={12} className={styles.rows}>
        <Flex justify="space-between" align="center" gap={16}>
          <Text type="secondary">{t("Receipt number")}</Text>
          <Text className={styles.val}>{SAMPLE.receiptNo}</Text>
        </Flex>
        <Flex justify="space-between" align="center" gap={16}>
          <Text type="secondary">{t("Payment method")}</Text>
          <Flex align="center" gap={8} className={styles.val}>
            <Icon type="card" style={{ fontSize: 16 }} />
            <Text className={styles.val}>•••• {SAMPLE.card}</Text>
          </Flex>
        </Flex>
        <div className={cls(styles.item, hl("business_details"))}>
          <Flex justify="space-between" align="center" gap={16}>
            <Text type="secondary">{t("Product or service")}</Text>
            <Text className={styles.val}>{SAMPLE.amount}</Text>
          </Flex>
          {productDescription ? (
            <Text type="secondary" className={styles.itemdesc}>
              {productDescription}
            </Text>
          ) : null}
        </div>
      </Flex>

      <Divider style={{ margin: "24px 0" }} />

      {/* Merchant + support footer. */}
      <Flex vertical align="center" gap={12} className={styles.footer}>
        {businessAddress ? (
          <Flex
            align="center"
            justify="center"
            gap={8}
            className={cls(styles.address, hl("business_details"))}
          >
            <Icon type="pin" style={{ fontSize: 16 }} />
            <Text type="secondary">{businessAddress}</Text>
          </Flex>
        ) : null}
        <Flex vertical align="center" gap={12} className={hl("support_legal")}>
          {supportPhone || supportEmail ? (
            <Text type="secondary" className={styles.support}>
              {[supportPhone, supportEmail].filter(Boolean).join(" · ")}
            </Text>
          ) : null}
          {support.refund_policy_url ||
          support.terms_url ||
          support.cancellation_policy_url ||
          support.support_website ? (
            <Flex gap={16} wrap="wrap" justify="center" className={styles.links}>
              {support.refund_policy_url ? (
                <a
                  href={withScheme(support.refund_policy_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {t("Refund policy")}
                </a>
              ) : null}
              {support.terms_url ? (
                <a
                  href={withScheme(support.terms_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {t("Terms of service")}
                </a>
              ) : null}
              {support.cancellation_policy_url ? (
                <a
                  href={withScheme(support.cancellation_policy_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {t("Cancellation policy")}
                </a>
              ) : null}
              {support.support_website ? (
                <a
                  href={withScheme(support.support_website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  {t("Support")}
                </a>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </div>
  )
}

export default ReceiptCard
